// src/components/RoyaltyWithdrawalManagement.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Refresh,
  AccountBalanceWallet,
  Email,
  Person,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { brandPalette as primaryBlue } from "../config/brandTheme";

const RoyaltyWithdrawalManagement = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [approveDialog, setApproveDialog] = useState({ open: false, withdrawal: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, withdrawal: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [processingId, setProcessingId] = useState(null);


  // Fetch withdrawal requests
  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setError("");

      const withdrawalsQuery = query(
        collection(db, "royaltyWithdrawalRequests"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(withdrawalsQuery);
      const withdrawalsData = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          
          // Fetch user's UPI ID
          let upiId = null;
          try {
            const userQuery = query(
              collection(db, "users"),
              where("uid", "==", data.userId)
            );
            const userSnapshot = await getDocs(userQuery);
            if (!userSnapshot.empty) {
              upiId = userSnapshot.docs[0].data().upiId || null;
            }
          } catch (err) {
            console.error("Error fetching UPI ID:", err);
          }

          return {
            id: docSnap.id,
            ...data,
            upiId,
          };
        })
      );

      setWithdrawals(withdrawalsData);
    } catch (err) {
      console.error("Error fetching withdrawal requests:", err);
      setError("Failed to fetch withdrawal requests: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  // Format date
  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  // Handle approval
  const handleApprove = async () => {
    if (!approveDialog.withdrawal) return;

    try {
      setProcessingId(approveDialog.withdrawal.id);
      setError("");
      setSuccess("");

      const withdrawal = approveDialog.withdrawal;
      
      // Verify user still has sufficient balance
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", withdrawal.userId)
      );
      const userSnapshot = await getDocs(userQuery);
      
      if (userSnapshot.empty) {
        setError("User not found");
        setApproveDialog({ open: false, withdrawal: null });
        return;
      }

      const userDoc = userSnapshot.docs[0];
      const userData = userDoc.data();
      const currentBalance = userData.royaltyBalance || 0;

      if (currentBalance < withdrawal.withdrawalAmount) {
        setError(`User's current balance (₹${currentBalance}) is less than requested withdrawal amount (₹${withdrawal.withdrawalAmount})`);
        setApproveDialog({ open: false, withdrawal: null });
        return;
      }

      // Update withdrawal request status
      await updateDoc(doc(db, "royaltyWithdrawalRequests", withdrawal.id), {
        status: "approved",
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Deduct from user's royalty balance
      await updateDoc(doc(db, "users", userDoc.id), {
        royaltyBalance: currentBalance - withdrawal.withdrawalAmount,
        royaltyUsed: (userData.royaltyUsed || 0) + withdrawal.withdrawalAmount,
        updatedAt: new Date().toISOString(),
      });

      setSuccess(`Withdrawal of ₹${withdrawal.withdrawalAmount} approved and deducted from user's royalty balance.`);
      setApproveDialog({ open: false, withdrawal: null });
      fetchWithdrawals();
    } catch (err) {
      console.error("Error approving withdrawal:", err);
      setError("Failed to approve withdrawal: " + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  // Handle rejection
  const handleReject = async () => {
    if (!rejectDialog.withdrawal) return;

    try {
      setProcessingId(rejectDialog.withdrawal.id);
      setError("");
      setSuccess("");

      await updateDoc(doc(db, "royaltyWithdrawalRequests", rejectDialog.withdrawal.id), {
        status: "rejected",
        rejectionReason: rejectionReason || "No reason provided",
        rejectedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      setSuccess("Withdrawal request rejected.");
      setRejectDialog({ open: false, withdrawal: null });
      setRejectionReason("");
      fetchWithdrawals();
    } catch (err) {
      console.error("Error rejecting withdrawal:", err);
      setError("Failed to reject withdrawal: " + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "pending":
        return <Chip label="Pending" color="warning" size="small" />;
      case "approved":
        return <Chip label="Approved" color="success" size="small" />;
      case "rejected":
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const pendingWithdrawals = withdrawals.filter((w) => w.status === "pending");
  const approvedWithdrawals = withdrawals.filter((w) => w.status === "approved");
  const rejectedWithdrawals = withdrawals.filter((w) => w.status === "rejected");

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ color: primaryBlue[800], fontWeight: 700 }}>
          Cash Withdrawal Requests
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchWithdrawals}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography variant="h4" color="warning.main">
              {pendingWithdrawals.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending Requests
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography variant="h4" color="success.main">
              {approvedWithdrawals.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approved
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography variant="h4" color="error.main">
              {rejectedWithdrawals.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rejected
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading withdrawal requests...
          </Typography>
        </Box>
      ) : withdrawals.length === 0 ? (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <AccountBalanceWallet sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Withdrawal Requests Found
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: primaryBlue[50] }}>
                <TableCell sx={{ fontWeight: 600 }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>UPI ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Requested Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Balance at Request
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map((withdrawal) => (
                <TableRow key={withdrawal.id} hover>
                  <TableCell>{formatDate(withdrawal.createdAt)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Person fontSize="small" color="action" />
                      {withdrawal.userName || "N/A"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      {withdrawal.userEmail || "N/A"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccountBalanceWallet fontSize="small" color="action" />
                      {withdrawal.upiId || "Not provided"}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#1E40AF" }}>
                    ₹{withdrawal.withdrawalAmount}
                  </TableCell>
                  <TableCell align="right">
                    ₹{withdrawal.currentBalance}
                  </TableCell>
                  <TableCell>{getStatusChip(withdrawal.status)}</TableCell>
                  <TableCell align="center">
                    {withdrawal.status === "pending" && (
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                        <Tooltip title="Approve Withdrawal">
                          <IconButton
                            color="success"
                            onClick={() =>
                              setApproveDialog({ open: true, withdrawal })
                            }
                            disabled={processingId === withdrawal.id}
                            size="small"
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject Withdrawal">
                          <IconButton
                            color="error"
                            onClick={() =>
                              setRejectDialog({ open: true, withdrawal })
                            }
                            disabled={processingId === withdrawal.id}
                            size="small"
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    {processingId === withdrawal.id && (
                      <CircularProgress size={20} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approve Dialog */}
      <Dialog
        open={approveDialog.open}
        onClose={() => setApproveDialog({ open: false, withdrawal: null })}
      >
        <DialogTitle>Approve Withdrawal Request</DialogTitle>
        <DialogContent>
          {approveDialog.withdrawal && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>User:</strong> {approveDialog.withdrawal.userName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {approveDialog.withdrawal.userEmail}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>UPI ID:</strong> {approveDialog.withdrawal.upiId || "Not provided"}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Requested Amount:</strong> ₹{approveDialog.withdrawal.withdrawalAmount}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Balance at Request:</strong> ₹{approveDialog.withdrawal.currentBalance}
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                This will deduct ₹{approveDialog.withdrawal.withdrawalAmount} from the
                user's royalty balance. Are you sure you want to approve this withdrawal?
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveDialog({ open: false, withdrawal: null })}
            disabled={processingId === approveDialog.withdrawal?.id}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={processingId === approveDialog.withdrawal?.id}
          >
            {processingId === approveDialog.withdrawal?.id ? "Processing..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => {
          setRejectDialog({ open: false, withdrawal: null });
          setRejectionReason("");
        }}
      >
        <DialogTitle>Reject Withdrawal Request</DialogTitle>
        <DialogContent>
          {rejectDialog.withdrawal && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>User:</strong> {rejectDialog.withdrawal.userName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Requested Amount:</strong> ₹{rejectDialog.withdrawal.withdrawalAmount}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Reason (Optional)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Enter reason for rejection..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialog({ open: false, withdrawal: null });
              setRejectionReason("");
            }}
            disabled={processingId === rejectDialog.withdrawal?.id}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={processingId === rejectDialog.withdrawal?.id}
          >
            {processingId === rejectDialog.withdrawal?.id ? "Processing..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoyaltyWithdrawalManagement;
